/**
 * Log Collector Utility
 *
 * Intercepts console methods and stores recent logs in memory for bug reports.
 * Call initLogCollector() early in the app to start collecting logs.
 */
import { IS_PRODUCTION } from "@/config/api";

export interface LogEntry {
  timestamp: string;
  level: "log" | "warn" | "error" | "info" | "debug";
  message: string;
  data?: unknown[];
}

// Configuration
const MAX_LOG_ENTRIES = 100;
const MAX_MESSAGE_LENGTH = 1000;

// Storage for collected logs
let collectedLogs: LogEntry[] = [];
let isInitialized = false;

// Store original console methods
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  info: console.info,
  debug: console.debug,
};

/**
 * Safely stringify a value for logging
 */
function safeStringify(value: unknown): string {
  try {
    if (value === undefined) return "undefined";
    if (value === null) return "null";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (value instanceof Error) {
      return `${value.name}: ${value.message}${value.stack ? `\n${value.stack}` : ""}`;
    }

    // For objects and arrays, try to stringify
    const str = JSON.stringify(
      value,
      (key, val) => {
        // Handle circular references and special types
        if (typeof val === "function") return "[Function]";
        if (val instanceof HTMLElement) return `[HTMLElement: ${val.tagName}]`;
        if (val instanceof Window) return "[Window]";
        if (val instanceof Document) return "[Document]";
        return val;
      },
      2
    );

    return str.length > MAX_MESSAGE_LENGTH
      ? str.substring(0, MAX_MESSAGE_LENGTH) + "...[truncated]"
      : str;
  } catch {
    return "[Unable to stringify]";
  }
}

/**
 * Format console arguments into a single message string
 */
function formatMessage(args: unknown[]): string {
  return args.map(safeStringify).join(" ");
}

/**
 * Add a log entry to the collection
 */
function addLogEntry(level: LogEntry["level"], args: unknown[]): void {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    message: formatMessage(args),
  };

  collectedLogs.push(entry);

  // Keep only the most recent entries
  if (collectedLogs.length > MAX_LOG_ENTRIES) {
    collectedLogs = collectedLogs.slice(-MAX_LOG_ENTRIES);
  }
}

/**
 * Create an interceptor for a console method
 */
function createInterceptor(
  level: LogEntry["level"],
  original: (...args: unknown[]) => void
): (...args: unknown[]) => void {
  return function (...args: unknown[]) {
    // Add to collected logs
    addLogEntry(level, args);
    if (!IS_PRODUCTION) {
      original.apply(console, args);
    }
  };
}

/**
 * Initialize the log collector by intercepting console methods.
 * Should be called once early in the application lifecycle.
 */
export function initLogCollector(): void {
  if (isInitialized) {
    return;
  }

  // Intercept console methods
  console.log = createInterceptor("log", originalConsole.log);
  console.warn = createInterceptor("warn", originalConsole.warn);
  console.error = createInterceptor("error", originalConsole.error);
  console.info = createInterceptor("info", originalConsole.info);
  console.debug = createInterceptor("debug", originalConsole.debug);

  // Add initial entry
  addLogEntry("info", ["[LogCollector] Initialized"]);

  // Capture unhandled errors
  if (typeof window !== "undefined") {
    window.addEventListener("error", (event) => {
      addLogEntry("error", [
        `[Unhandled Error] ${event.message}`,
        `at ${event.filename}:${event.lineno}:${event.colno}`,
      ]);
    });

    window.addEventListener("unhandledrejection", (event) => {
      addLogEntry("error", ["[Unhandled Promise Rejection]", event.reason]);
    });
  }

  isInitialized = true;
}

/**
 * Get all collected logs
 */
export function getCollectedLogs(): LogEntry[] {
  return [...collectedLogs];
}

/**
 * Get collected logs as a formatted string for bug reports
 */
export function getLogsAsString(): string {
  return collectedLogs
    .map((entry) => `[${entry.timestamp}] [${entry.level.toUpperCase()}] ${entry.message}`)
    .join("\n");
}

/**
 * Clear all collected logs
 */
export function clearLogs(): void {
  collectedLogs = [];
}

/**
 * Detect if the device is mobile/touch-capable
 * Uses multiple detection methods for maximum compatibility
 */
function detectIsMobile(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  // Method 1: User-Agent Client Hints API (most accurate, Chromium-only: Chrome, Edge, Opera)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userAgentData = (navigator as any).userAgentData;
  if (userAgentData?.mobile !== undefined) {
    return userAgentData.mobile;
  }

  // Method 2: Check maxTouchPoints (works in most modern browsers including Safari, Firefox)
  if ("maxTouchPoints" in navigator && navigator.maxTouchPoints > 0) {
    return true;
  }

  // Method 3: Check for coarse pointer (touch screens)
  if (typeof matchMedia !== "undefined") {
    const coarsePointer = matchMedia("(pointer:coarse)");
    if (coarsePointer?.media === "(pointer:coarse)" && coarsePointer.matches) {
      return true;
    }

    // Method 4: Check if device lacks hover capability (touch devices)
    const noHover = matchMedia("(hover: none)");
    if (noHover?.matches) {
      return true;
    }
  }

  // Method 5: Fallback to userAgent patterns for older browsers
  const mobileUA =
    /\b(Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone|Mobile|Tablet)\b/i;
  if (mobileUA.test(navigator.userAgent)) {
    return true;
  }

  return false;
}

/**
 * Parse OS name and version from the user agent string.
 * Uses regex heuristics; UA-CH (when available) overrides this with more
 * accurate values via {@link getHighEntropyDeviceInfo}.
 */
function parseOS(): { name: string; version: string } {
  if (typeof navigator === "undefined") {
    return { name: "unknown", version: "unknown" };
  }

  const ua = navigator.userAgent;

  // iOS / iPadOS (UA still reports the version even on iPadOS pre-13)
  const iosMatch = ua.match(/(?:iPhone|iPad|iPod).+?OS (\d+[._]\d+(?:[._]\d+)?)/);
  if (iosMatch) {
    return { name: "iOS", version: iosMatch[1].replace(/_/g, ".") };
  }

  // iPadOS masquerading as Macintosh (Safari on iPad >= 13 by default)
  const isIpadMasquerading =
    /Macintosh/.test(ua) &&
    "maxTouchPoints" in navigator &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  if (isIpadMasquerading) {
    return { name: "iPadOS", version: "unknown" };
  }

  const androidMatch = ua.match(/Android (\d+(?:\.\d+){0,2})/);
  if (androidMatch) {
    return { name: "Android", version: androidMatch[1] };
  }

  const chromeOsMatch = ua.match(/CrOS \S+ (\d+(?:\.\d+){0,2})/);
  if (chromeOsMatch) {
    return { name: "ChromeOS", version: chromeOsMatch[1] };
  }

  const winMatch = ua.match(/Windows NT (\d+\.\d+)/);
  if (winMatch) {
    // Windows 11 reports NT 10.0 in the legacy UA - we cannot distinguish
    // it from Windows 10 without UA-CH platformVersion.
    const winMap: Record<string, string> = {
      "10.0": "10/11",
      "6.3": "8.1",
      "6.2": "8",
      "6.1": "7",
      "6.0": "Vista",
      "5.1": "XP",
    };
    return { name: "Windows", version: winMap[winMatch[1]] || winMatch[1] };
  }

  const macMatch = ua.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/);
  if (macMatch) {
    return { name: "macOS", version: macMatch[1].replace(/_/g, ".") };
  }

  if (/X11.*Linux/.test(ua)) {
    return { name: "Linux", version: "unknown" };
  }

  return { name: "unknown", version: "unknown" };
}

/**
 * Best-effort device model extraction from the user agent.
 * - Android: extracts the device codename (e.g. "SM-S918B").
 * - iOS / Mac / Windows: only generic class names are exposed by the browser.
 * UA-CH (when available) overrides this for Android via high-entropy `model`.
 */
function parseDeviceModel(): string {
  if (typeof navigator === "undefined") {
    return "unknown";
  }

  const ua = navigator.userAgent;

  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/iPod/.test(ua)) return "iPod";

  // iPadOS masquerading as Mac
  if (
    /Macintosh/.test(ua) &&
    "maxTouchPoints" in navigator &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1
  ) {
    return "iPad";
  }

  // Android model - typical UA: "(Linux; Android 13; SM-S918B Build/...)"
  const androidModelMatch = ua.match(
    /Android \d+(?:\.\d+){0,2};\s*(?:[a-z]{2}-[a-z]{2};\s*)?([^;)]+?)(?:\s+Build|\)|;)/i
  );
  if (androidModelMatch) {
    const model = androidModelMatch[1].trim();
    if (model && !/^(Mobile|Tablet)$/i.test(model)) {
      return model;
    }
  }
  if (/Android/.test(ua)) return "Android device";

  if (/CrOS/.test(ua)) return "Chromebook";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "PC";
  if (/Linux/.test(ua)) return "Linux PC";

  return "unknown";
}

interface HighEntropyDeviceInfo {
  platform?: string;
  platformVersion?: string;
  model?: string;
  architecture?: string;
  bitness?: string;
}

/**
 * Fetch high-entropy User-Agent Client Hints (Chromium-only).
 * Provides accurate platformVersion (e.g. real macOS version) and Android
 * device model. Returns null when the API is unavailable or fails.
 */
async function getHighEntropyDeviceInfo(): Promise<HighEntropyDeviceInfo | null> {
  if (typeof navigator === "undefined") {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userAgentData = (navigator as any).userAgentData;
  if (!userAgentData || typeof userAgentData.getHighEntropyValues !== "function") {
    return null;
  }
  try {
    const hints = await userAgentData.getHighEntropyValues([
      "platform",
      "platformVersion",
      "model",
      "architecture",
      "bitness",
    ]);
    return hints as HighEntropyDeviceInfo;
  } catch (error) {
    console.warn("[logCollector.getHighEntropyDeviceInfo] Failed to read UA-CH:", error);
    return null;
  }
}

/**
 * Parse browser name and version from user agent string
 */
function parseBrowser(): { name: string; version: string } {
  if (typeof navigator === "undefined") {
    return { name: "unknown", version: "unknown" };
  }

  const ua = navigator.userAgent;

  // Order matters - check more specific browsers first
  const browsers: Array<{ name: string; pattern: RegExp }> = [
    { name: "Edge", pattern: /Edg(?:e|A|iOS)?\/(\d+[\d.]*)/ },
    { name: "Opera", pattern: /(?:OPR|Opera)\/(\d+[\d.]*)/ },
    { name: "Samsung Internet", pattern: /SamsungBrowser\/(\d+[\d.]*)/ },
    { name: "UC Browser", pattern: /UCBrowser\/(\d+[\d.]*)/ },
    { name: "Firefox", pattern: /Firefox\/(\d+[\d.]*)/ },
    { name: "Chrome", pattern: /Chrome\/(\d+[\d.]*)/ },
    { name: "Safari", pattern: /Version\/(\d+[\d.]*).*Safari/ },
    { name: "IE", pattern: /(?:MSIE |rv:)(\d+[\d.]*)/ },
  ];

  for (const browser of browsers) {
    const match = ua.match(browser.pattern);
    if (match) {
      return { name: browser.name, version: match[1] || "unknown" };
    }
  }

  return { name: "unknown", version: "unknown" };
}

/**
 * Get device type classification
 */
function getDeviceType(): string {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "unknown";
  }

  const ua = navigator.userAgent.toLowerCase();
  const width = window.innerWidth;

  // Check for tablet patterns first
  if (/ipad|tablet|playbook|silk/i.test(ua) || (!/mobile/i.test(ua) && /android/i.test(ua))) {
    return "tablet";
  }

  // Check for mobile patterns
  if (
    /mobile|iphone|ipod|android.*mobile|windows phone|blackberry|opera mini|opera mobi/i.test(ua)
  ) {
    return "mobile";
  }

  // Fallback based on screen width and touch
  const isTouchCapable = detectIsMobile();
  if (isTouchCapable && width < 768) {
    return "mobile";
  }
  if (isTouchCapable && width >= 768 && width < 1024) {
    return "tablet";
  }

  return "desktop";
}

/**
 * Get browser and environment information for bug reports.
 * Synchronous so it can be used from error boundaries and crash handlers
 * before things fall apart further. For the (richer) async-enriched version
 * used by the manual bug-report dialog, see {@link getBugReportData}.
 */
export function getBrowserInfo(): Record<string, string | number | boolean> {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { environment: "server" };
  }

  const browser = parseBrowser();
  const isMobile = detectIsMobile();
  const deviceType = getDeviceType();
  const parsedOS = parseOS();

  return {
    // Device & touch info
    isMobile,
    isTouchDevice: isMobile,
    deviceType,
    deviceModel: parseDeviceModel(),

    // OS info
    os: parsedOS.name,
    osVersion: parsedOS.version,

    // Browser info
    browser: browser.name,
    browserVersion: browser.version,
    userAgent: navigator.userAgent,

    // Screen & viewport
    screenWidth: window.screen?.width ?? 0,
    screenHeight: window.screen?.height ?? 0,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    orientation:
      window.screen?.orientation?.type ||
      (window.innerWidth > window.innerHeight ? "landscape" : "portrait"),

    // System info
    language: navigator.language,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Page info
    currentUrl: window.location.href,
    referrer: document.referrer || "none",
  };
}

/**
 * Apply UA-CH high-entropy values on top of base browser info, when available.
 * UA-CH is Chromium-only and async; this gives more accurate OS version and
 * Android device model than UA-string parsing.
 */
async function enrichBrowserInfoWithUACH(
  base: Record<string, string | number | boolean>
): Promise<Record<string, string | number | boolean>> {
  const hints = await getHighEntropyDeviceInfo();
  if (!hints) return base;

  const enriched = { ...base };

  if (hints.platformVersion) {
    enriched.osVersion = hints.platformVersion;
    // Windows: UA-CH platformVersion >= 13.0.0 means Windows 11
    // (Microsoft's documented heuristic). Otherwise it's still Windows 10.
    const platform = (hints.platform || String(base.os || "")).toLowerCase();
    if (platform === "windows") {
      const major = parseInt(hints.platformVersion.split(".")[0] ?? "", 10);
      if (!Number.isNaN(major)) {
        enriched.os = major >= 13 ? "Windows 11" : "Windows 10";
      }
    }
  }

  if (hints.model && hints.model.trim()) {
    enriched.deviceModel = hints.model.trim();
  }

  if (hints.architecture) enriched.architecture = hints.architecture;
  if (hints.bitness) enriched.bitness = hints.bitness;

  return enriched;
}

/**
 * Get a complete bug report payload, enriched with UA-CH high-entropy values
 * when the browser supports them (Chromium).
 */
export async function getBugReportData(): Promise<{
  logs: LogEntry[];
  browserInfo: Record<string, string | number | boolean>;
  timestamp: string;
}> {
  return {
    logs: getCollectedLogs(),
    browserInfo: await enrichBrowserInfoWithUACH(getBrowserInfo()),
    timestamp: new Date().toISOString(),
  };
}
